import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import type { UserSettings } from './types'
import { useLang, type Lang } from '../i18n'

/** TanStack key of GET /v1/me/settings. */
export const USER_SETTINGS_KEY = ['me', 'settings'] as const

/**
 * The caller's own settings (GET /v1/me/settings, AB#388) — the preferences that follow a member
 * across browsers and machines. `retry: false`: a failure leaves the local cache in charge, and
 * the next start asks again.
 */
export function useUserSettings() {
  return useQuery({
    queryKey: USER_SETTINGS_KEY,
    queryFn: async () => (await api.meSettings()).settings,
    staleTime: Infinity,
    retry: false,
  })
}

/** PATCH /v1/me/settings; the answer (the merged map) becomes the cached query value. */
export function usePatchUserSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (partial: UserSettings) => (await api.patchMeSettings(partial)).settings,
    onSuccess: (settings) => qc.setQueryData(USER_SETTINGS_KEY, settings),
  })
}

const isLang = (v: unknown): v is Lang => v === 'en' || v === 'fr'

/**
 * Keeps the UI language in step with the member's server-side setting. LOCAL-FIRST: `niobe.lang`
 * (lib/lang-prefs.ts) is the cache LangProvider applies at startup, so nothing waits on the network.
 *
 * - When the GET resolves holding a `lang`, it wins (and LangProvider writes it back to the cache).
 * - When it resolves with none (first run after the deploy), the local value is PATCHed once, so an
 *   existing member keeps their choice.
 * - A change afterwards (the TopBar EN/FR toggle) has already updated the cache; it is PATCHed
 *   fire-and-forget. A failed PATCH is swallowed — no prompt, no toast; the next change resends.
 * - A change made BEFORE the GET resolved is the member's latest word: it is PATCHed, not overridden.
 *
 * Mount once, inside the signed-in tree (the calls need the Bearer).
 */
export function useLangSettingsSync(): void {
  const { lang, setLang } = useLang()
  const settings = useUserSettings()
  const { mutate } = usePatchUserSettings()
  /** The value the server holds as far as we know — `undefined` until the GET resolved. */
  const serverLang = useRef<Lang | null | undefined>(undefined)
  /** The language the member picked while the GET was still out. */
  const pickedEarly = useRef(false)
  const initialLang = useRef(lang)

  const send = (next: Lang): void => {
    serverLang.current = next
    mutate({ lang: next }, { onError: () => { /* swallowed: the next change resends */ } })
  }

  // The GET resolved: apply the server value, or seed it with the local one.
  useEffect(() => {
    if (!settings.data || serverLang.current !== undefined) return
    const remote = settings.data.lang
    if (pickedEarly.current || !isLang(remote)) {
      send(lang)
    } else {
      serverLang.current = remote
      if (remote !== lang) setLang(remote)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, when the GET first resolves
  }, [settings.data])

  // The GET failed: the server value stays unknown, but later changes are still sent.
  useEffect(() => {
    if (!settings.isError || serverLang.current !== undefined) return
    serverLang.current = null
    if (pickedEarly.current) send(lang)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, when the GET fails
  }, [settings.isError])

  // A language change: PATCH it once the server value is known, else remember it for the GET.
  useEffect(() => {
    if (serverLang.current === undefined) {
      if (lang !== initialLang.current) pickedEarly.current = true
      return
    }
    if (lang !== serverLang.current) send(lang)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `send` is stable in effect
  }, [lang])
}

/** Renders nothing: mounts {@link useLangSettingsSync} where a component is the natural seam. */
export function LangSettingsSync(): null {
  useLangSettingsSync()
  return null
}
